import React, { useState } from 'react';
import { fetchResponse } from '../services';
import { parseXml } from '../helpers';
import { CollectionItem } from '../objects';
import LoadingInline from './loading-inline.js';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const BggTrade = () => {
    
    const [user1, setUser1] = useState('');
    const [user2, setUser2] = useState('');
    const [matchedGames, setMatchedGames] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [progressValues, setProgressValues] = useState({message: "Loading", progress: 0, type: 'progress'});
    
    // Checkboxes state
    const [user1Filters, setUser1Filters] = useState({
        collection: true,
        wantlist: false,
        tradelist: false,
        wantintrade: false,
        wanttobuy: false
    });
    
    const [user2Filters, setUser2Filters] = useState({
        collection: false,
        wantlist: true,
        tradelist: false,
        wantintrade: false,
        wanttobuy: false
    });

    // Handle user1 input change
    const handleUser1Change = (event) => {
        setUser1(event.target.value);
    };

    // Handle user2 input change
    const handleUser2Change = (event) => {
        setUser2(event.target.value);
    };

    // Handle checkbox changes for user1
    const handleUser1FilterChange = (event) => {
        setUser1Filters({
            ...user1Filters,
            [event.target.name]: event.target.checked
        });
    };

    // Handle checkbox changes for user2
    const handleUser2FilterChange = (event) => {
        setUser2Filters({
            ...user2Filters,
            [event.target.name]: event.target.checked
        });
    };

    // Fetch collection data with retry logic
    const fetchCollectionData = async (username, filters) => {
        let loadingMessage = `Loading ${username}'s collection, attempt 1`;
        setProgressValues({message: loadingMessage, progress: 0, type: "spinner"});
        
        let iterations = 1;
        let result = null;
        
        // Build query parameters based on filters
        let queryParams = `username=${username}`;
        
        if (filters.collection) {
            queryParams += '&own=1';
        }
        
        if (filters.wantlist) {
            queryParams += '&wishlist=1';
        }
        
        if (filters.tradelist) {
            queryParams += '&trade=1';
        }
        
        if (filters.wantintrade) {
            queryParams += '&want=1';
        }
        
        if (filters.wanttobuy) {
            queryParams += '&wanttobuy=1';
        }
        
        // Retry logic
        while (true) {
            result = await fetchResponse(`/xmlapi2/collection?${queryParams}`);
            
            if (result.status !== 200) {
                if (iterations > 4) {
                    console.error(`Exceeded attempt limit for ${username}`);
                    break;
                }
                
                // wait 5 seconds and try again
                await new Promise(resolve => setTimeout(resolve, 5000));
                
                iterations++;
                loadingMessage = `Loading ${username}'s collection, attempt ${iterations}`;
                setProgressValues({message: loadingMessage, progress: (iterations/5)*100, type: "spinner"});
                continue;
            } else {
                break;
            }
        }
        
        if (result.status !== 200) {
            console.error(`Failed to load collection data for ${username}`);
            return [];
        }
        
        // Parse XML data and create CollectionItem objects
        let xmlDoc = await parseXml(result.data);
        const items = xmlDoc.getElementsByTagName('item');
        let collectionItems = [];
        
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const collectionItem = CollectionItem.fromXml(item);
            collectionItems.push(collectionItem);
        }
        
        return collectionItems;
    };

    // Find matches between the two collections
    const findMatches = (collection1, collection2, filters1, filters2) => {
        const matches = [];
        
        // Filter collection1 based on filters1 using CollectionItem method
        const filteredCollection1 = collection1.filter(item => item.matchesFilters(filters1));
        
        // Filter collection2 based on filters2 using CollectionItem method
        const filteredCollection2 = collection2.filter(item => item.matchesFilters(filters2));
        
        // Find matches where user1's games match user2's criteria
        filteredCollection1.forEach(item1 => {
            const matchingItem = filteredCollection2.find(item2 => item2.id === item1.id);
            
            if (matchingItem) {
                matches.push({
                    id: item1.id,
                    name: item1.name,
                    user1Status: {
                        own: item1.status.own,
                        wishlist: item1.status.wishlist,
                        trade: item1.status.trade,
                        wantintrade: item1.status.wantintrade,
                        wanttobuy: item1.status.wanttobuy
                    },
                    user2Status: {
                        own: matchingItem.status.own,
                        wishlist: matchingItem.status.wishlist,
                        trade: matchingItem.status.trade,
                        wantintrade: matchingItem.status.wantintrade,
                        wanttobuy: matchingItem.status.wanttobuy
                    }
                });
            }
        });
        
        return matches;
    };

    // Handle the compare button click
    const handleCompare = async () => {
        if (!user1 || !user2) {
            alert('Please enter both usernames');
            return;
        }
        
        setIsLoading(true);
        
        try {
            // Fetch user1's collection
            const user1Data = await fetchCollectionData(user1, user1Filters);
            
            // Fetch user2's collection
            const user2Data = await fetchCollectionData(user2, user2Filters);
            
            // Find matches
            const matches = findMatches(user1Data, user2Data, user1Filters, user2Filters);
            setMatchedGames(matches);
            
        } catch (error) {
            console.error('Error fetching collection data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Define column definitions for the grid
    const columnDefs = [
        { headerName: 'Game', field: 'name', flex: 1, minWidth: 200, filter: true },
        { 
            headerName: `${user1} Has`, 
            children: [
                { 
                    headerName: 'Collection', 
                    field: 'user1Status.own', 
                    width: 120, 
                    filter: true,
                    cellRenderer: params => params.value ? '✓' : ''
                },
                { 
                    headerName: 'Wishlist', 
                    field: 'user1Status.wishlist', 
                    width: 120, 
                    filter: true,
                    cellRenderer: params => params.value ? '✓' : ''
                },
                { 
                    headerName: 'For Trade', 
                    field: 'user1Status.trade', 
                    width: 120, 
                    filter: true,
                    cellRenderer: params => params.value ? '✓' : ''
                },
                { 
                    headerName: 'Want in Trade', 
                    field: 'user1Status.wantintrade', 
                    width: 120, 
                    filter: true,
                    cellRenderer: params => params.value ? '✓' : ''
                },
                { 
                    headerName: 'Want to Buy', 
                    field: 'user1Status.wanttobuy', 
                    width: 120, 
                    filter: true,
                    cellRenderer: params => params.value ? '✓' : ''
                }
            ]
        },
        { 
            headerName: `${user2} Has`, 
            children: [
                { 
                    headerName: 'Collection', 
                    field: 'user2Status.own', 
                    width: 120, 
                    filter: true,
                    cellRenderer: params => params.value ? '✓' : ''
                },
                { 
                    headerName: 'Wishlist', 
                    field: 'user2Status.wishlist', 
                    width: 120, 
                    filter: true,
                    cellRenderer: params => params.value ? '✓' : ''
                },
                { 
                    headerName: 'For Trade', 
                    field: 'user2Status.trade', 
                    width: 120, 
                    filter: true,
                    cellRenderer: params => params.value ? '✓' : ''
                },
                { 
                    headerName: 'Want in Trade', 
                    field: 'user2Status.wantintrade', 
                    width: 120, 
                    filter: true,
                    cellRenderer: params => params.value ? '✓' : ''
                },
                { 
                    headerName: 'Want to Buy', 
                    field: 'user2Status.wanttobuy', 
                    width: 120, 
                    filter: true,
                    cellRenderer: params => params.value ? '✓' : ''
                }
            ]
        },
        { 
            headerName: 'BGG Link', 
            field: 'id', 
            width: 100,
            cellRenderer: params => {
                return `<a href="https://boardgamegeek.com/boardgame/${params.value}" target="_blank">View</a>`;
            }
        }
    ];

    return (
        <div className="app-container">
            <div className="controls-panel">
                <div className="control-group">
                    <div className="control-item">
                        <div className="control-input-group">
                            <input 
                                type="text" 
                                placeholder="BGG Username 1" 
                                value={user1} 
                                onChange={handleUser1Change}
                                className="control-input" 
                            />
                            <div className="checkbox-group">
                                <label>
                                    <input 
                                        type="checkbox" 
                                        name="collection" 
                                        checked={user1Filters.collection} 
                                        onChange={handleUser1FilterChange} 
                                    />
                                    Collection
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        name="wantlist" 
                                        checked={user1Filters.wantlist} 
                                        onChange={handleUser1FilterChange} 
                                    />
                                    Want List
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        name="tradelist" 
                                        checked={user1Filters.tradelist} 
                                        onChange={handleUser1FilterChange} 
                                    />
                                    Trade List
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        name="wantintrade" 
                                        checked={user1Filters.wantintrade} 
                                        onChange={handleUser1FilterChange} 
                                    />
                                    Want in Trade
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        name="wanttobuy" 
                                        checked={user1Filters.wanttobuy} 
                                        onChange={handleUser1FilterChange} 
                                    />
                                    Want to Buy
                                </label>
                            </div>
                        </div>
                    </div>
                    
                    <div className="control-divider"></div>
                    
                    <div className="control-item">
                        <div className="control-input-group">
                            <input 
                                type="text" 
                                placeholder="BGG Username 2" 
                                value={user2} 
                                onChange={handleUser2Change}
                                className="control-input" 
                            />
                            <div className="checkbox-group">
                                <label>
                                    <input 
                                        type="checkbox" 
                                        name="collection" 
                                        checked={user2Filters.collection} 
                                        onChange={handleUser2FilterChange} 
                                    />
                                    Collection
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        name="wantlist" 
                                        checked={user2Filters.wantlist} 
                                        onChange={handleUser2FilterChange} 
                                    />
                                    Want List
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        name="tradelist" 
                                        checked={user2Filters.tradelist} 
                                        onChange={handleUser2FilterChange} 
                                    />
                                    Trade List
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        name="wantintrade" 
                                        checked={user2Filters.wantintrade} 
                                        onChange={handleUser2FilterChange} 
                                    />
                                    Want in Trade
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        name="wanttobuy" 
                                        checked={user2Filters.wanttobuy} 
                                        onChange={handleUser2FilterChange} 
                                    />
                                    Want to Buy
                                </label>
                            </div>
                        </div>
                    </div>
                    
                    <div className="control-divider"></div>
                    
                    <div className="control-item">
                        <button 
                            onClick={handleCompare} 
                            className="control-button primary-button"
                            disabled={isLoading}
                        >
                            Compare
                        </button>
                    </div>
                </div>
            </div>
            
            {isLoading && (
                <LoadingInline 
                    message={progressValues.message} 
                    progress={progressValues.progress} 
                    type={progressValues.type} 
                />
            )}
            
            <div className="table-info">
                <div className="row-count">Matches Found: {matchedGames.length}</div>
            </div>
            
            <div className="table-container">
                <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
                    <AgGridReact
                        columnDefs={columnDefs}
                        rowData={matchedGames}
                        className="data-grid"
                    >
                    </AgGridReact>
                </div>
            </div>
        </div>
    );
};

export default BggTrade;